<template>
  <div class="org-settings-documents pb-6 relative">
    
    <!-- Header -->
    <div class="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h2 class="text-xl font-bold text-gray-800">Documents & Pitches</h2>
        <p class="text-sm text-gray-500 mt-1">Upload files to share on your organization's public profile.</p>
      </div>
      
      <!-- Top Right Upload Button (Optional quick access) -->
      <!-- <vs-button 
        v-if="documents.length > 0"
        color="primary" 
        icon-pack="feather" 
        icon="icon-upload" 
        class="font-semibold"
        @click="$refs.fileInput.click()"
      >
        Upload File
      </vs-button> -->
    </div>

    <!-- Upload File Dropzone -->
<div
  class="relative group w-full min-h-[320px] mb-10
         rounded-2xl
         border-2 border-dashed border-gray-300
         bg-white
         overflow-hidden
         cursor-pointer
         transition-all duration-300 ease-out
         hover:border-primary/60
         hover:bg-gray-50/40
         hover:shadow-[0_8px_30px_rgba(0,0,0,0.05)]"
  @click="$refs.fileInput.click()"
  @dragover.prevent="dragOver = true"
  @dragleave.prevent="dragOver = false"
  @drop.prevent="handleDrop"
  :class="{
    'border-primary bg-primary/[0.04] shadow-[0_8px_30px_rgba(240,191,70,0.12)]':
      dragOver
  }"
>
  <input
    type="file"
    class="hidden"
    ref="fileInput"
    @change="handleFileUpload"
    multiple
    accept=".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
  />

  <!-- Background decoration -->
  <div
    class="absolute -top-24 -right-24
           w-72 h-72
           rounded-full
           bg-primary/[0.045]
           blur-3xl
           pointer-events-none
           transition-all duration-500
           group-hover:bg-primary/[0.08]"
  ></div>

  <div
    class="absolute -bottom-28 -left-24
           w-72 h-72
           rounded-full
           bg-blue-500/[0.025]
           blur-3xl
           pointer-events-none"
  ></div>

  <!-- Content -->
  <div
    class="relative z-10
           flex flex-col items-center justify-center
           min-h-[320px]
           px-10 py-12"
  >

    <!-- Icon -->
    <div
      class="flex items-center justify-center
             w-[76px] h-[76px]
             mb-7
             rounded-2xl
             bg-primary/[0.08]
             border border-primary/15
             transition-all duration-300
             group-hover:-translate-y-1
             group-hover:bg-primary/[0.12]
             group-hover:border-primary/25
             group-hover:shadow-[0_10px_25px_rgba(240,191,70,0.15)]"
    >
      <feather-icon
        icon="UploadCloudIcon"
        class="w-10 h-10 text-primary
               transition-transform duration-300
               group-hover:-translate-y-1"
      />
    </div>

    <!-- Heading -->
    <h3
      class="text-[23px]
             font-extrabold
             tracking-[-0.025em]
             text-gray-900
             mb-3"
    >
      Upload your documents
    </h3>

    <!-- Description -->
    <p
      class="text-[14px]
             text-gray-500
             leading-relaxed
             mb-7"
    >
      Drag and drop your files here, or
      <span
        class="font-bold text-primary
               group-hover:underline
               underline-offset-4"
      >
        browse from your computer
      </span>
    </p>

    <!-- Supported formats -->
    <div
      class="flex items-center
             justify-center
             gap-2"
    >

      <span
        class="px-3.5 py-2
               rounded-lg
               bg-gray-50
               border border-gray-200
               text-[11px]
               font-bold
               text-gray-500"
      >
        PDF
      </span>

      <span
        class="px-3.5 py-2
               rounded-lg
               bg-gray-50
               border border-gray-200
               text-[11px]
               font-bold
               text-gray-500"
      >
        PPTX
      </span>

      <span
        class="px-3.5 py-2
               rounded-lg
               bg-gray-50
               border border-gray-200
               text-[11px]
               font-bold
               text-gray-500"
      >
        DOCX
      </span>

      <span
        class="px-3.5 py-2
               rounded-lg
               bg-gray-50
               border border-gray-200
               text-[11px]
               font-bold
               text-gray-500"
      >
        XLSX
      </span>

      <span
        class="px-3.5 py-2
               rounded-lg
               bg-gray-50
               border border-gray-200
               text-[11px]
               font-bold
               text-gray-500"
      >
        IMG
      </span><br>

      <span
        class="ml-2
               text-[11px]
               font-semibold
               text-gray-400"
      >
        (Maximum size 5MB)
      </span>

    </div>

    <!-- Bottom hint -->
    <div
      class="mt-8
             flex items-center gap-2
             text-[11px]
             font-medium
             text-gray-400"
    >
      <span
        class="w-1.5 h-1.5
               rounded-full
               bg-primary"
      ></span>

      Multiple files can be uploaded at once
    </div>

  </div>

  <!-- Drag overlay -->
  <div
    v-if="dragOver"
    class="absolute inset-0 z-20
           flex items-center justify-center
           bg-white/90
           backdrop-blur-sm
           pointer-events-none"
  >
    <div class="text-center">

      <div
        class="mx-auto mb-4
               flex items-center justify-center
               w-20 h-20
               rounded-2xl
               bg-primary/10
               border border-primary/20"
      >
        <feather-icon
          icon="UploadCloudIcon"
          class="w-10 h-10 text-primary"
        />
      </div>

      <h4
        class="text-lg font-bold text-gray-900"
      >
        Drop your files here
      </h4>

      <p
        class="mt-1 text-sm text-gray-500"
      >
        Release to start uploading
      </p>

    </div>
  </div>
</div>

    <!-- Uploaded Documents Grid (2 per row) -->
    <div v-if="documents.length > 0">
      <h3 class="text-md font-bold text-gray-800 mb-4 border-b border-gray-100 pb-2">Manage Uploaded Files</h3>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div 
          v-for="(doc, index) in documents" 
          :key="doc.id || index"
          class="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow gap-4 relative overflow-hidden"
        >
          <!-- Left: Icon & Details -->
          <div class="flex items-center gap-3 min-w-0">
            <!-- Dynamic File Icon based on extension -->
            <div :class="['p-3 rounded-xl shrink-0', getFileBgColor(doc.file_extension)]">
              <feather-icon :icon="getFileIcon(doc.file_extension)" :class="['w-6 h-6', getFileIconColor(doc.file_extension)]" />
            </div>
            
            <div class="min-w-0 flex flex-col justify-center pr-2">
              <h4 class="font-bold text-gray-800 text-[15px] truncate" :title="doc.original_filename">
                {{ doc.original_filename }}
              </h4>
              <div class="flex items-center gap-2 text-[11px] text-gray-500 mt-1 font-medium">
                <span class="uppercase tracking-wider font-bold">{{ doc.file_extension }}</span>
                <span>•</span>
                <span>{{ doc.file_size }}</span>
              </div>
            </div>
          </div>

          <!-- Right: Action Icons (Preview, Visibility, Delete) -->
          <div class="flex items-center gap-2 shrink-0 border-t border-gray-50 sm:border-none pt-3 sm:pt-0 w-full sm:w-auto justify-end">
            
            <!-- 1. Preview Button (With Loading State if converting) -->
            <vs-button 
              v-if="doc.pdf_file_url || isImageFile(doc.file_extension)"
              color="primary" 
              type="border" 
              icon-pack="feather" 
              icon="icon-maximize-2" 
              size="small"
              class="rounded-lg bg-blue-50/50 hover:bg-primary hover:text-white transition-colors"
              title="Preview Document"
              @click="previewDocument(doc)"
            ></vs-button>

            <!-- Converting State -->
            <div v-else class="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg border border-yellow-100 text-xs font-bold" title="Converting to PDF for preview...">
              <feather-icon icon="LoaderIcon" class="w-3 h-3 animate-spin" />
              Processing
            </div>

            <!-- 2. Visibility Toggle Button -->
            <vs-button 
              :color="doc.is_public ? 'success' : 'dark'" 
              type="border" 
              icon-pack="feather" 
              :icon="doc.is_public ? 'icon-eye' : 'icon-eye-off'" 
              size="small"
              class="rounded-lg transition-colors"
              :class="doc.is_public ? 'bg-green-50 hover:bg-success hover:text-white' : 'bg-gray-50 hover:bg-dark hover:text-white'"
              :title="doc.is_public ? 'Visible on Public Profile' : 'Hidden from Public Profile'"
              @click="toggleVisibility(doc)"
            ></vs-button>

            <!-- 3. Delete Button -->
            <vs-button 
              color="danger" 
              type="border" 
              icon-pack="feather" 
              icon="icon-trash-2" 
              size="small"
              class="rounded-lg bg-red-50 hover:bg-danger hover:text-white transition-colors"
              title="Delete Document"
              @click="removeDocument(doc.id, index)"
            ></vs-button>

          </div>
        </div>
      </div>
    </div>
    
    <!-- Empty State -->
    <div v-else class="text-center py-16 border border-gray-100 rounded-2xl bg-gray-50/50">
      <feather-icon icon="FileTextIcon" class="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h4 class="text-gray-500 font-bold text-lg">No documents uploaded yet</h4>
      <p class="text-gray-400 text-sm mt-1">Upload files above to manage them here.</p>
    </div>

    <!-- PDF Preview Modal -->
    <vs-popup title="Document Preview" :active.sync="previewActive" class="mega-preview-modal">
      <div v-if="previewActive" class="w-full flex flex-col items-center justify-center bg-gray-100" style="height: 600px; max-height: 80vh;">
        
        <!-- Render Image -->
        <img 
          v-if="previewIsImage" 
          :src="currentPreviewUrl" 
          class="max-w-full max-h-full object-contain p-4"
          alt="Preview"
        />

        <!-- Render PDF in Iframe -->
        <iframe 
          v-else
          :src="currentPreviewUrl + '#toolbar=0&navpanes=0&scrollbar=0'" 
          class="flex-grow w-full border-0 bg-gray-100 rounded-b-lg"
          frameborder="0"
        ></iframe>
        
      </div>
    </vs-popup>

  </div>
</template>

<script>
export default {
  name: "OrgSettingsDocuments",
  data() {
    return {
      dragOver: false,
      documents: [],
      previewActive: false,
      previewIsImage: false, // <-- ADD THIS
      currentPreviewUrl: "",
      pollInterval: null
    };
  },
  computed: {
    orgId() {
      return this.$store.state.organization?.orgData?._id || "";
    },
    apiUrl() {
      const url = process.env.VUE_APP_API_URL || "http://127.0.0.1:8000";
      return url.endsWith('/') ? url.slice(0, -1) : url;
    },
    // Checks if any document is currently missing its PDF (still converting)
    isConverting() {
      return this.documents.some(doc => {
        const ext = (doc.file_extension || '').toLowerCase();
        const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
        // Only consider it "converting" if it's NOT an image AND missing a PDF url
        return !isImg && !doc.pdf_file_url; 
      });
    }
  },
  mounted() {
    if (this.orgId) {
      this.fetchDocuments();
    }
  },
  beforeDestroy() {
    this.stopPolling();
  },
  watch: {
    // Watch the 'isConverting' computed property to start/stop polling
    isConverting(newValue) {
      if (newValue) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    }
  },
  methods: {
    async fetchDocuments(silent = false) {
      try {
        const url = `${this.apiUrl}/api/v2/organization/${this.orgId}/documents`;
        const res = await this.$http.get(url);
        if (res.data.status === 'success') {
          this.documents = res.data.data.map(d => ({
            ...d, 
            is_public: Boolean(d.is_public)
          }));
        }
      } catch (error) {
        if (!silent) console.error("Failed to fetch documents", error);
      }
    },

    // Polling Mechanism: Silently checks the DB every 3 seconds for the converted PDF URL
    startPolling() {
      if (this.pollInterval) return;
      this.pollInterval = setInterval(() => {
        this.fetchDocuments(true); // Silent fetch (no loading screen)
      }, 3000);
    },
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    handleFileUpload(event) {
      const files = event.target.files;
      if (!files.length) return;
      this.processFiles(files);
      this.$refs.fileInput.value = ''; // Reset input
    },

    handleDrop(event) {
      this.dragOver = false;
      const files = event.dataTransfer.files;
      if (!files.length) return;
      this.processFiles(files);
    },

    async processFiles(files) {
      const MAX_SIZE = 5 * 1024 * 1024; // 5 MB
      const filesArray = Array.from(files);

      // Separate valid files and oversized files
      const validFiles = filesArray.filter(f => f.size <= MAX_SIZE);
      const invalidFiles = filesArray.filter(f => f.size > MAX_SIZE);

      // Warn user about oversized files
      if (invalidFiles.length > 0) {
        this.$vs.notify({ 
          title: 'Files Too Large', 
          text: `${invalidFiles.length} file(s) exceeded the 5MB limit and were skipped.`, 
          color: 'danger', 
          iconPack: 'feather', 
          icon: 'icon-alert-circle' 
        });
      }

      if (validFiles.length === 0) return;

      // Show loader once for the whole batch
      this.$vs.loading({ text: `Uploading ${validFiles.length} file(s)...` });
      
      try {
        // Create an array of upload requests
        const uploadPromises = validFiles.map(file => {
          const formData = new FormData();
          formData.append("file", file);
          const url = `${this.apiUrl}/api/v2/organization/${this.orgId}/documents`;
          return this.$http.post(url, formData, { 
            headers: { 'Content-Type': 'multipart/form-data' } 
          });
        });

        // Execute all uploads simultaneously
        await Promise.all(uploadPromises);
        
        this.$vs.notify({ 
          title: 'Success', 
          text: 'All documents uploaded successfully.', 
          color: 'success',
          iconPack: 'feather', 
          icon: 'icon-check' 
        });
        
        // Refresh the document list ONCE after everything is done
        this.fetchDocuments(); 
        
      } catch (error) {
        this.$vs.notify({ 
          title: 'Upload Failed', 
          text: 'An error occurred while uploading some files.', 
          color: 'danger',
          iconPack: 'feather', 
          icon: 'icon-alert-circle'
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async toggleVisibility(doc) {
      const newStatus = !doc.is_public;
      doc.is_public = newStatus; // Optimistic update
      
      try {
        const url = `${this.apiUrl}/api/v2/organization/${this.orgId}/documents/${doc.id}/visibility`;
        await this.$http.patch(url, { is_public: newStatus });
      } catch (error) {
        doc.is_public = !newStatus; // Revert
        this.$vs.notify({ 
          title: 'Error', text: 'Failed to update visibility.', color: 'danger', iconPack: 'feather', icon: 'icon-alert-circle'
        });
      }
    },

    async removeDocument(docId, index) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: `Confirm Delete`,
        text: 'Are you sure you want to delete this document?',
        acceptText: 'Delete',
        accept: async () => {
          try {
            const url = `${this.apiUrl}/api/v2/organization/${this.orgId}/documents/${docId}`;
            await this.$http.delete(url);
            this.documents.splice(index, 1);
          } catch (error) {
            this.$vs.notify({ 
              title: 'Error', text: 'Failed to delete the document.', color: 'danger', iconPack: 'feather', icon: 'icon-alert-circle'
            });
          }
        }
      });
    },

    isImageFile(ext) {
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes((ext || '').toLowerCase());
    },

    previewDocument(doc) {
      if (!doc) return;
      
      if (this.isImageFile(doc.file_extension)) {
        // It's an image, use the original URL directly
        this.currentPreviewUrl = `${this.apiUrl}${doc.original_file_url}`;
        this.previewIsImage = true;
      } else {
        // It's a document, use the PDF URL
        if (!doc.pdf_file_url) return;
        this.currentPreviewUrl = `${this.apiUrl}${doc.pdf_file_url}`;
        this.previewIsImage = false;
      }
      
      this.previewActive = true;
    },
    
    // --- UI Helpers for File Types ---
    getFileIcon(ext) {
      const e = (ext || '').toLowerCase();
      switch (e) {
        case 'pdf': return 'FileTextIcon';
        case 'ppt': 
        case 'pptx': return 'MonitorIcon';
        case 'doc': 
        case 'docx': return 'FileIcon';
        case 'xls': 
        case 'xlsx': 
        case 'csv': return 'GridIcon';
        case 'png':
        case 'jpg':
        case 'jpeg': return 'ImageIcon';
        default: return 'FileIcon';
      }
    },
    getFileBgColor(ext) {
      const e = (ext || '').toLowerCase();
      switch (e) {
        case 'pdf': return 'bg-red-50';
        case 'ppt': 
        case 'pptx': return 'bg-orange-50';
        case 'doc': 
        case 'docx': return 'bg-blue-50';
        case 'xls': 
        case 'xlsx': 
        case 'csv': return 'bg-green-50';
        case 'png':
        case 'jpg':
        case 'jpeg': return 'bg-purple-50';
        default: return 'bg-gray-100';
      }
    },
    getFileIconColor(ext) {
      const e = (ext || '').toLowerCase();
      switch (e) {
        case 'pdf': return 'text-red-600';
        case 'ppt': 
        case 'pptx': return 'text-orange-600';
        case 'doc': 
        case 'docx': return 'text-blue-600';
        case 'xls': 
        case 'xlsx': 
        case 'csv': return 'text-green-600';
        case 'png':
        case 'jpg':
        case 'jpeg': return 'text-purple-600';
        default: return 'text-gray-600';
      }
    }
  }
};
</script>

<style scoped>
/* 
  AGGRESSIVE CSS TO FIX VUESAX MODAL SIZE 
  Using deep selector to force Vuesax popup to take up 95% of the screen
*/
::v-deep .mega-preview-modal .vs-popup {
  width: 95vw !important;
  max-width: 1400px !important;
  height: 92vh !important;
  display: flex !important;
  flex-direction: column !important;
  margin: auto !important;
  border-radius: 12px;
  overflow: hidden;
}

::v-deep .mega-preview-modal .vs-popup--content {
  flex-grow: 1 !important;
  padding: 0 !important; /* Remove inner padding so iframe is flush */
  margin: 0 !important;
  height: 100% !important;
  width: 100% !important;
  overflow: hidden !important;
  display: flex;
}

::v-deep .mega-preview-modal .vs-popup--header {
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 10px;
}
</style>